import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { communityApi } from '../../api/community'
import Header from '../../components/layout/Header'
import Footer from '../../components/layout/Footer'
import Button from '../../components/common/Button'
import Input from '../../components/common/Input'
import Card from '../../components/common/Card'
import './CreatePost.css'

const CreatePost = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [content, setContent] = useState('')
  const [image, setImage] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  const [error, setError] = useState('')

  const createMutation = useMutation({
    mutationFn: (data) => communityApi.createPost(data),
    onSuccess: (response) => {
      // Invalidate posts list to refresh
      queryClient.invalidateQueries(['community-posts'])
      
      // Navigate to the newly created post or back to community
      const postId = response?.data?.id || response?.data?.post?.id
      if (postId) {
        navigate(`/community/posts/${postId}`)
      } else {
        navigate('/community')
      }
    },
    onError: (error) => {
      setError(error.response?.data?.message || 'حدث خطأ أثناء إنشاء المنشور')
    },
  })

  const handleImageChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('يجب اختيار ملف صورة')
        return
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('حجم الصورة يجب أن يكون أقل من 5 ميجابايت')
        return
      }

      setImage(file)
      setError('')
      
      // Create preview
      const reader = new FileReader()
      reader.onloadend = () => {
        setImagePreview(reader.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleRemoveImage = () => {
    setImage(null)
    setImagePreview(null)
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setError('')

    if (!content.trim()) {
      setError('يرجى إدخال محتوى المنشور')
      return
    }

    const formData = new FormData()
    formData.append('content', content)
    if (image) {
      formData.append('image', image)
    }

    createMutation.mutate(formData)
  }

  return (
    <div className="create-post-page">
      <Header />
      <main className="create-post-main">
        <div className="container">
          <div className="create-post-header">
            <h1 className="page-title">إنشاء منشور جديد</h1>
            <Button variant="secondary" onClick={() => navigate('/community')}>
              إلغاء
            </Button>
          </div>

          <Card className="create-post-card">
            <form onSubmit={handleSubmit} className="create-post-form">
              {error && (
                <div className="error-message">
                  <p>{error}</p>
                </div>
              )}

              <div className="form-group">
                <label htmlFor="content">محتوى المنشور</label>
                <textarea
                  id="content"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="اكتب ما يدور في ذهنك..."
                  rows={8}
                  className="content-textarea"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="image">صورة (اختياري)</label>
                <div className="image-upload-section">
                  {imagePreview ? (
                    <div className="image-preview-wrapper">
                      <img src={imagePreview} alt="Preview" className="image-preview" />
                      <button
                        type="button"
                        onClick={handleRemoveImage}
                        className="remove-image-btn"
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <label htmlFor="image" className="image-upload-label">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <polyline points="21 15 16 10 5 21" />
                      </svg>
                      <span>اختر صورة</span>
                      <input
                        type="file"
                        id="image"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="image-input"
                      />
                    </label>
                  )}
                </div>
              </div>

              <div className="form-actions">
                <Button
                  type="submit"
                  variant="primary"
                  loading={createMutation.isPending}
                  disabled={!content.trim() || createMutation.isPending}
                >
                  نشر
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => navigate('/community')}
                  disabled={createMutation.isPending}
                >
                  إلغاء
                </Button>
              </div>
            </form>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  )
}

export default CreatePost

